"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";

export interface CollapsibleProps {
  /** The question or header text */
  title: React.ReactNode;
  /** The hidden content */
  children: React.ReactNode;
  /** Optional icon to display next to the title */
  icon?: React.ReactNode;
  /** Whether the component is open by default */
  defaultOpen?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Custom class for the container */
  className?: string;
  /** Custom class for the header button */
  headerClassName?: string;
  /** Custom class for the content wrapper */
  contentClassName?: string;
}

/**
 * A neubrutalist collapsible component for FAQs and progressive disclosure.
 */
export function Collapsible({
  title,
  children,
  icon,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  className,
  headerClassName,
  contentClassName,
}: CollapsibleProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  };

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-black bg-white transition-all shadow-[0px_4px_0px_0px_#000000]",
        className
      )}
    >
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "flex w-full items-center justify-between p-4 text-left transition-colors bg-[#ED5AA9] text-white",
          headerClassName
        )}
      >
        <div className="flex items-center gap-3">
          {icon && (
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white text-white">
              {icon}
            </div>
          )}
          <span className="text-sm font-bold">{title}</span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 shrink-0 text-white" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className={cn("border-t border-black p-4 text-sm text-[#475467] leading-relaxed", contentClassName)}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
